import React from 'react';
import { Column } from 'react-table';
import { formatCurrency } from 'utils';
import { Proposal } from '@trnsact/trnsact-shared-types';
import { DateCell } from 'components/Table/Cells/DateCell';
import { DateFilter } from 'components/Table/Filters/DateFilter';
import { InputFilter } from 'components/Table/Filters/InputFilter';
import { getPersonaName } from 'modules/proposals/lib/getPersonaName';
import { SimpleTextCell } from 'components/Table/Cells/SimpleTextCell';
import { CellArgs, ProposalTableActions } from 'modules/proposals/types';
import { searchFilterByColumn } from 'components/Table/lib/searchFilters';
import { ActionCell, StatusCell, DealTypeCell } from 'modules/proposals/ui/tables/cells';
import { MultiSelectFilter } from 'components/Table/Filters/ColumnMultiSelectFilter';
import { dealTypeToLabel } from 'modules/proposals';

interface Options extends ProposalTableActions<Proposal> {
  tableData?: Proposal[];
}

type ProposalCellArgs = CellArgs<Proposal>;

export function createTableColumns({
  tableData = [],
  onGoToProposal,
  onGoToProposalDetail,
}: Options): Column<Proposal>[] {
  return [
    {
      width: 200,
      sortable: true,
      resizable: false,
      filterable: true,
      Header: 'Created On',
      accessor: 'createdDateTime',
      headerClassName: 'customHeader',
      filterMethod: searchFilterByColumn,
      Cell: ({ original }: ProposalCellArgs) => <DateCell value={original.createdDateTime!} />,
      Filter: filterProps => <DateFilter<Proposal> {...filterProps} rows={tableData} />,
    },
    {
      sortable: true,
      resizable: false,
      filterable: true,
      Header: 'Dealer',
      accessor: 'account.name',
      headerClassName: 'customHeader',
      Cell: ({ original }: ProposalCellArgs) => <SimpleTextCell value={original.account?.name} />,
      Filter: (filterProps: any) => <MultiSelectFilter {...filterProps} rows={tableData} />,
    },
    {
      sortable: true,
      resizable: false,
      filterable: true,
      Header: 'Company',
      accessor: 'businessName',
      headerClassName: 'customHeader',
      filterMethod: searchFilterByColumn,
      Cell: ({ original }: ProposalCellArgs) => <SimpleTextCell value={original.businessName!} />,
      Filter: (filterProps: any) => <MultiSelectFilter {...filterProps} rows={tableData} />,
    },
    {
      sortable: true,
      resizable: false,
      filterable: true,
      Header: 'Contact',
      accessor: 'firstName',
      headerClassName: 'customHeader',
      filterMethod: searchFilterByColumn,
      Cell: ({ original }: ProposalCellArgs) => <SimpleTextCell value={getPersonaName(original)} />,
      Filter: filterProps => <MultiSelectFilter {...filterProps} rows={tableData} />,
    },
    {
      sortable: true,
      resizable: false,
      filterable: true,
      Header: 'Amount',
      accessor: 'amountRequested',
      headerClassName: 'customHeader',
      Cell: ({ original }: ProposalCellArgs) => (
        <SimpleTextCell formatter={formatCurrency} value={original.amountRequested!} />
      ),
      Filter: filterProps => <InputFilter {...filterProps} rows={tableData} />,
    },
    {
      sortable: true,
      resizable: false,
      filterable: true,
      accessor: 'status',
      Header: 'Proposal Status',
      style: { minWidth: '11rem' },
      headerClassName: 'customHeader',
      headerStyle: { minWidth: '11rem' },
      filterMethod: searchFilterByColumn,
      Cell: ({ original }: ProposalCellArgs) => <StatusCell proposal={original} />,
      Filter: filterProps => <MultiSelectFilter {...filterProps} rows={tableData} />,
    },
    {
      sortable: true,
      resizable: false,
      filterable: true,
      accessor: 'lead.inboundDealType',
      Header: 'Deal Type',
      style: { minWidth: '11rem' },
      headerClassName: 'customHeader',
      headerStyle: { minWidth: '11rem' },
      filterMethod: searchFilterByColumn,
      Cell: ({ original }: ProposalCellArgs) => <DealTypeCell type={(original.lead as any)?.inboundDealType ?? null} />,
      Filter: filterProps => (
        <MultiSelectFilter {...filterProps} labelFormatter={v => dealTypeToLabel[v] ?? v} rows={tableData} />
      ),
    },
    {
      sortable: false,
      Header: 'Actions',
      headerClassName: 'customHeader',
      Cell: ({ original }: ProposalCellArgs) => (
        <ActionCell proposal={original} onGoToProposal={onGoToProposal} onGoToProposalDetail={onGoToProposalDetail} />
      ),
    },
  ];
}
